import {
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  EntityRelationWarning,
  EntitySwitch,
  hasCatalogProcessingErrors,
  hasRelationWarnings,
  isOrphan,
} from '@backstage/plugin-catalog';
import type { EntityContentLayoutProps } from '@backstage/plugin-catalog-react/alpha';
import styles from './EntityOverviewLayout.module.css';

/**
 * Overview layout for every entity, in two parts.
 *
 * `info` cards - About, Links, Labels - are the entity's own metadata: short,
 * uneven, and complete on the first paint. They stack down a rail at their
 * natural height, so a two-link Links card is simply a small card.
 *
 * Everything else fetches what it shows. Those tile the rest of the width in
 * equal fixed-height boxes, which keeps their rows aligned and stops the page
 * reflowing as each one's data lands.
 *
 * The rail comes first in the markup, so when the columns collapse on a
 * narrow screen the metadata is already on top with no reordering needed.
 *
 * Card order within each part is set in `app-config.yaml` under
 * `app.extensions`: extensions named there resolve first, in the order
 * listed, and everything unlisted follows.
 */
export function EntityOverviewLayout({ cards }: EntityContentLayoutProps) {
  const infoCards = cards.filter(card => card.type === 'info');
  const dataCards = cards.filter(card => card.type !== 'info');

  return (
    <>
      <div className={styles.warnings}>
        <EntitySwitch>
          <EntitySwitch.Case if={isOrphan}>
            <EntityOrphanWarning />
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={hasRelationWarnings}>
            <EntityRelationWarning />
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={hasCatalogProcessingErrors}>
            <EntityProcessingErrorsPanel />
          </EntitySwitch.Case>
        </EntitySwitch>
      </div>
      <div className={styles.grid}>
        {infoCards.length > 0 && (
          <div className={styles.rail}>
            {infoCards.map((card, index) => (
              <div className={styles.infoCard} key={index}>
                {card.element}
              </div>
            ))}
          </div>
        )}
        <div className={styles.content}>
          {dataCards.map((card, index) => (
            <div className={styles.card} key={index}>
              {card.element}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
